import type { BattleDetails } from "../battle/reportTypes.js";

export type Tier = "S" | "A" | "B" | "C" | "D";
export type ModuleSlot = "weapon" | "armor" | "core" | "sensor" | "drive";
export type SquadSlotKey = "commander" | "f1" | "f2" | "f3" | "f4" | "s1" | "s2";
export const SQUAD_SLOT_KEYS: SquadSlotKey[] = ["commander", "f1", "f2", "f3", "f4", "s1", "s2"];

export interface StatBlock {
  hp: number;
  atk: number;
  def: number;
  intervalTicks: number;
  crit: number;
  critDmg: number;
  acc: number;
  eva: number;
  energyPerAttack: number;
  cargo: number;
}

export interface ModuleView {
  id: string;
  moduleId: string;
  slot: ModuleSlot;
  tier: Tier;
  mfr: string;
  enhance: number;
  main: { stat: string; value: number; mode: "pct" | "flat" };
  subs: { stat: string; value: number; mode: "pct" | "flat" }[];
  power: number;
  unitRowId: string | null;
  nextEnhance: { SCR: number; nano: number } | null;
}

export interface AbilityView {
  index: number;
  archetype: string;
  name: { de: string; en: string };
  kind: string;
  level: number;
  cap: number;
  nextCost: number | null;
  params: Record<string, number>;
}

export interface UnitView {
  id: string;
  unitId: string;
  level: number;
  xp: number;
  xpToNext: number;
  levelCap: number;
  stars: number;
  abilities: AbilityView[];
  stats: StatBlock;
  power: number;
  hpPct: number;
  wrecked: boolean;
  repair: { startedAt: number; finishesAt: number; remainingMs: number } | null;
  modules: ModuleView[];
  squad: number | null;
  squadSlot: string | null;
  fragments: number;
  nextStar: { star: number; cost: number; have: number; ufr: number } | null;
  chemistryPct: number;
  skin: string | null;
}

export interface SquadView {
  slot: number;
  name: string;
  members: Record<SquadSlotKey, string | null>;
  chemistry: { perUnit: Record<string, { points: number; pct: number; links: { to: string; kind: string }[] }>; totalPoints: number; percent: number; doctrines: string[] };
  power: number;
  garrison: boolean;
  ready: boolean;
  siegeRunId?: string | null;
  wrecked: number;
}

export interface CraftView {
  id: string;
  moduleId: string;
  startedAt: number;
  finishesAt: number;
  remainingMs: number;
}

export interface RosterState {
  units: UnitView[];
  squads: SquadView[];
  maxSquads: number;
  modules: ModuleView[];
  repair: { slots: number; used: number; speedPct: number; bayLevel: number };
  craft: { slots: number; used: number; queue: CraftView[]; maxTier: string; armoryLevel: number; speedPct: number };
  collection: { owned: string[]; milestones: { id: string; reached: boolean; claimed: boolean; progress: number; target: number }[] };
  caps: { hqLevel: number; hangarLevel: number; abilityCap: number };
}

export interface BannerView {
  id: "standard" | "featured" | "manufacturer";
  name: { de: string; en: string };
  token: string | null;
  tokensHave: number;
  singleCredits: number;
  tenTokens: number;
  tenCredits: number;
  featuredUnitId: string | null;
  manufacturer: string | null;
  rotationKey?: string;
  rotationEndsAt?: number | null;
  promotion?: { runId: string; manufacturer: string; featuredUnitId: string; statBonusPct: number; endsAt: number; changesAt: number } | null;
  pool: string[];
  rates: Record<Tier, number>;
  pity: { sinceS: number; sinceA: number; hardPity: number; softPityStart: number; featuredGuaranteed: boolean; pulls: number };
  free: { available: boolean; period: "day" | "week"; vipLevel: number; nextKey: string } | null;
}

export interface PullView {
  unitId: string;
  tier: Tier;
  isNew: boolean;
  fragments: number;
  flags: { hardPity: boolean; softPity: boolean; aGuarantee: boolean; featuredWin: boolean; featuredLoss: boolean };
}

export interface Loot {
  resources: Record<string, number>;
  items: { itemId: string; qty: number }[];
  credits: number;
  xp: number;
}

export interface UnitSummary {
  id: string;
  defId: string;
  side: "attacker" | "defender";
  slot: string;
  damageDealt: number;
  damageTaken: number;
  healing: number;
  kills: number;
  abilitiesUsed: number;
  alive: boolean;
  hpPct: number;
}

export interface BattleSummary {
  winner: "attacker" | "defender";
  outcome: "attacker" | "defender" | "timeout";
  ticks: number;
  units: UnitSummary[];
  mvp: string | null;
  hpShare: { attacker: number; defender: number };
  structures: { wallAbsorbed: number; turretDamage: number; trapDamage: number };
  hash: string;
}

export interface BattleOutcome {
  battleId: string;
  won: boolean;
  summary: BattleSummary;
  loot: Loot;
}

export interface BattleListEntry {
  id: string;
  context: string;
  won: boolean;
  opponentId: string | null;
  opponentName: string | null;
  ticks: number;
  mvp: string | null;
  createdAt: number;
}

export interface BattleReport {
  id: string;
  accountId: string;
  opponentId: string | null;
  opponentName: string | null;
  context: string;
  seed: number;
  createdAt: number;
  won: boolean;
  input: unknown;
  summary: BattleSummary;
  loot: Loot;
  /** Optional for cached reports captured before the presentation metadata migration. */
  details?: BattleDetails;
  waves?: { input: unknown; summary: BattleSummary }[];
}

export interface ArenaState {
  rating: number;
  band: { id: string; name: { de: string; en: string }; color: string; min: number };
  attempts: { used: number; max: number; refillCredits: number };
  refreshCredits: number;
  coins: number;
  wins: number;
  losses: number;
  opponents: { accountId: string; name: string; rating: number; band: string; power: number; cooldownUntil: number | null }[];
  defenceSlot: number;
  season: { weekKey: string; rank: number; endsAt: number; reward: { coins: number; cdc: number; fab: number } };
  top: { accountId: string; name: string; rating: number; band: string }[];
}

export interface ArenaLogEntry {
  id: string;
  opponentId: string;
  opponentName: string;
  attacker: boolean;
  won: boolean;
  ratingDelta: number;
  battleId: string;
  createdAt: number;
}

export interface RewardView {
  resources: Record<string, number>;
  items: { itemId: string; qty: number }[];
  credits: number;
}

export interface ExpeditionStageView {
  id: string;
  chapter: number;
  stage: number;
  boss: boolean;
  stamina: number;
  packLevel: number;
  power: number;
  stars: number;
  clears: number;
  unlocked: boolean;
  sweepable: boolean;
  firstClear: RewardView;
  repeat: RewardView;
  story: { panelId: string; title: { de: string; en: string }; lines: { de: string; en: string } } | null;
}

export interface ExpeditionState {
  stamina: { current: number; cap: number; nextAt: number };
  chapters: { chapter: number; title: { de: string; en: string }; stars: number; chests: { index: number; stars: number; claimed: boolean; claimable: boolean }[] }[];
  stages: ExpeditionStageView[];
  sweeps: { freeLeft: number; tickets: number };
}
